import React, {useEffect, useMemo, useRef} from "react";
import {Button} from "primereact/button";
import {OverlayPanel} from "primereact/overlaypanel";
import {Checkbox} from "primereact/checkbox";
import Icon from "../../system/components/icon";
import {useTranslate} from "../../system/useTranslate";
import {useDispatch, useSelector} from "react-redux";
import {setWorkspaceMembership} from "../../system/app-slice";
import WorkspaceMembership from "../../models/workspace-membership/workspace-membership";

export function isFixedTab(tab, index) {
    return index === 0 || tab.status === "disable";
}

export function useSelectableTabs(tabs, scope, activeIndex, setActiveIndex) {
    const dispatch = useDispatch();
    const workspaceMembership = useSelector(state => state.app.workspaceMembershipData);
    const requestIdRef = useRef(0);
    const tabSignature = tabs.map(tab => `${tab.id}:${tab.status || "visible"}`).join("|");
    const defaultIds = useMemo(
        () => tabs.filter((tab, index) => !isFixedTab(tab, index) && tab.status !== "invisible").map(tab => tab.id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tabSignature]
    );
    const configurableIds = useMemo(
        () => new Set(tabs.filter((tab, index) => !isFixedTab(tab, index)).map(tab => tab.id)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tabSignature]
    );
    const savedIds = workspaceMembership?.workspaceConfigs?.tabVisibility?.[scope];
    const selectedIds = Array.isArray(savedIds)
        ? [...new Set(savedIds)].filter(id => configurableIds.has(id))
        : defaultIds;
    const visibleTabs = tabs.filter((tab, index) => isFixedTab(tab, index) || selectedIds.includes(tab.id));

    useEffect(() => {
        setActiveIndex(0);
    }, [workspaceMembership?.documentId, workspaceMembership?.id, setActiveIndex]);

    useEffect(() => {
        if (!visibleTabs[activeIndex]) setActiveIndex(0);
    }, [activeIndex, setActiveIndex, visibleTabs]);

    const toggleTab = async (nextIds) => {
        const next = tabs
            .filter((tab, index) => !isFixedTab(tab, index) && nextIds.includes(tab.id))
            .map(tab => tab.id);
        if (next.length === selectedIds.length && next.every((id, index) => id === selectedIds[index])) return;
        const currentTabId = visibleTabs[activeIndex]?.id;
        const nextVisibleTabs = tabs.filter((tab, index) => isFixedTab(tab, index) || next.includes(tab.id));
        const nextActiveIndex = nextVisibleTabs.findIndex(tab => tab.id === currentTabId);
        setActiveIndex(nextActiveIndex >= 0 ? nextActiveIndex : 0);

        const membershipId = workspaceMembership?.documentId || workspaceMembership?.id;
        if (!membershipId) return;

        const previousWorkspaceConfigs = workspaceMembership.workspaceConfigs;
        const workspaceConfigs = {
            ...(previousWorkspaceConfigs || {}),
            tabVisibility: {
                ...(previousWorkspaceConfigs?.tabVisibility || {}),
                [scope]: next,
            },
        };
        const optimisticMembership = {...workspaceMembership, workspaceConfigs};
        const requestId = ++requestIdRef.current;
        dispatch(setWorkspaceMembership(optimisticMembership));

        try {
            const response = await new WorkspaceMembership().updateItem(membershipId, {workspaceConfigs});
            if (requestId !== requestIdRef.current) return;
            const savedWorkspaceConfigs = (response?.data || response)?.workspaceConfigs || workspaceConfigs;
            dispatch(setWorkspaceMembership({...optimisticMembership, workspaceConfigs: savedWorkspaceConfigs}));
        } catch (error) {
            if (requestId === requestIdRef.current) {
                dispatch(setWorkspaceMembership({...workspaceMembership, workspaceConfigs: previousWorkspaceConfigs}));
            }
        }
    };

    return {visibleTabs, selectedIds, toggleTab};
}

export default function SelectableTabsButton({tabs, selectedIds, onChange, className = "", style}) {
    const translate = useTranslate();
    const overlayRef = useRef(null);
    const buttonRef = useRef(null);
    const changeTab = (tabId) => {
        const next = selectedIds.includes(tabId)
            ? selectedIds.filter(id => id !== tabId)
            : [...selectedIds, tabId];
        onChange(next);
    };

    return (
        <>
            <Button ref={buttonRef} type="button" icon={<Icon icon="mi mi:settings" className="text-lg"/>}
                    className={`p-button-rounded p-button-text p-button-secondary shadow-none border-1 border-200 surface-0 hover:surface-100 ${className}`}
                    style={{width: "2.25rem", height: "2.25rem", ...style}}
                    tooltip={translate("tabs.customize")} tooltipOptions={{position: "bottom"}}
                    onClick={(event) => overlayRef.current.toggle(event)} aria-label={translate("tabs.customize")}/>
            <OverlayPanel ref={overlayRef} className="rtl border-round-xl shadow-4"
                          onShow={() => requestAnimationFrame(() => {
                              const button = buttonRef.current?.getElement?.();
                              const panel = overlayRef.current?.getElement?.();
                              if (!button || !panel) return;
                              const rect = button.getBoundingClientRect();
                              panel.style.top = `${rect.bottom + window.scrollY + 4}px`;
                              panel.style.left = `${rect.left + window.scrollX}px`;
                          })}
                          pt={{content: () => ({className: "p-2"})}}>
                <div className="flex flex-column gap-1" style={{minWidth: "14rem"}}>
                    <div className="font-bold text-sm text-900 px-2 py-2">{translate("tabs.customize")}</div>
                    {tabs.map((tab, index) => {
                        const fixed = isFixedTab(tab, index);
                        const checked = fixed || selectedIds.includes(tab.id);
                        return (
                            <div key={tab.id} onClick={() => !fixed && changeTab(tab.id)}
                                 className={`flex align-items-center gap-2 px-2 py-2 border-round-lg ${fixed ? "text-400" : "cursor-pointer hover:surface-100"}`}>
                                <Checkbox inputId={`select-tab-${tab.id}`} checked={checked} disabled={fixed}
                                          onClick={event => event.stopPropagation()} onChange={() => changeTab(tab.id)}/>
                                {tab.icon && <Icon icon={tab.icon} className="text-lg"/>}
                                <label htmlFor={`select-tab-${tab.id}`} className={fixed ? "" : "cursor-pointer"}>{translate(tab.label)}</label>
                            </div>
                        );
                    })}
                </div>
            </OverlayPanel>
        </>
    );
}
